import { useEffect, useState } from "react";
import { Sparkle, CheckCircle, Package, ArrowRight, FileText } from "@phosphor-icons/react";

/**
 * DemoShowcase — self-contained landing-page product demo.
 * Auto-cycles through the real product flow: Brief → AI Designs → Order.
 * No external API calls; purely visual mockup using the app's design language.
 */

const STEPS = [
  { key: "brief", label: "01 · Submit brief", duration: 6000 },
  { key: "generate", label: "02 · AI generates designs", duration: 7000 },
  { key: "order", label: "03 · Approve & order", duration: 6000 },
];

const TOTAL = STEPS.reduce((s, x) => s + x.duration, 0);

export default function DemoShowcase() {
  const [stepIdx, setStepIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const tick = 100;
    const t = setInterval(() => {
      setElapsed((e) => {
        const next = (e + tick) % TOTAL;
        let acc = 0;
        for (let i = 0; i < STEPS.length; i++) {
          acc += STEPS[i].duration;
          if (next < acc) { setStepIdx(i); break; }
        }
        return next;
      });
    }, 100);
    return () => clearInterval(t);
  }, []);

  const progress = (elapsed / TOTAL) * 100;
  const cur = STEPS[stepIdx].key;

  return (
    <section className="border-b border-[var(--pf-border)] bg-[var(--pf-surface-2)]" data-testid="landing-demo">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-4">
            <div className="pf-label">Live · Product Demo</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="text-4xl md:text-5xl font-black tracking-tighter">
              Watch a brief become <span className="text-[var(--pf-brand)]">print-ready</span> in real time.
            </h2>
            <p className="mt-6 text-[var(--pf-text-2)] text-lg max-w-2xl">
              No slides, no fluff. Here's the actual product loop — three steps, one minute, zero WhatsApp.
            </p>
          </div>
        </div>

        {/* Browser chrome */}
        <div className="border-2 border-[var(--pf-text)] bg-white shadow-[8px_8px_0_0_var(--pf-text)]">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--pf-border)] bg-white">
            <span className="w-3 h-3 rounded-full bg-[#FF5F57]"></span>
            <span className="w-3 h-3 rounded-full bg-[#FEBC2E]"></span>
            <span className="w-3 h-3 rounded-full bg-[#28C840]"></span>
            <div className="flex-1 flex justify-center">
              <div className="font-mono text-xs text-[var(--pf-text-2)] bg-[var(--pf-surface-2)] px-4 py-1 border border-[var(--pf-border)]">
                talaio.ai / {cur === "brief" ? "brief/new" : cur === "generate" ? "brief/PF-24601" : "orders/PF-24601"}
              </div>
            </div>
            <div className="pf-label hidden md:block">DEMO</div>
          </div>

          {/* Step tabs */}
          <div className="grid grid-cols-3 border-b border-[var(--pf-border)]">
            {STEPS.map((s, i) => (
              <div key={s.key} className={`p-4 text-center border-r last:border-0 border-[var(--pf-border)] transition-colors ${i === stepIdx ? "bg-[var(--pf-text)] text-white" : "bg-white"}`}>
                <div className={`text-[10px] tracking-widest uppercase font-bold ${i === stepIdx ? "text-white" : "text-[var(--pf-text-2)]"}`}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Stage */}
          <div className="min-h-[420px] p-6 md:p-10 relative overflow-hidden">
            {cur === "brief" && <BriefStage />}
            {cur === "generate" && <GenerateStage />}
            {cur === "order" && <OrderStage />}
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-[var(--pf-border)] relative">
            <div className="absolute inset-y-0 left-0 bg-[var(--pf-brand)] transition-all duration-100 ease-linear" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------- Stage 1: Brief -------- */
function BriefStage() {
  const [typed, setTyped] = useState("");
  const target = "Sales Team Business Cards — Q1 2026";
  useEffect(() => {
    setTyped("");
    let i = 0;
    const t = setInterval(() => {
      i++;
      setTyped(target.slice(0, i));
      if (i >= target.length) clearInterval(t);
    }, 55);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 animate-[fadeIn_.4s_ease-out]">
      <div className="md:col-span-7">
        <div className="pf-label mb-2">Step 1 of 3</div>
        <h3 className="text-3xl font-black tracking-tighter mb-6">New brief.</h3>
        <div className="mb-5">
          <div className="pf-label mb-1">Project title</div>
          <div className="border-b-2 border-[var(--pf-text)] pb-1 text-lg font-medium min-h-[32px]">
            {typed}<span className="animate-pulse">|</span>
          </div>
        </div>
        <div className="pf-label mb-2">Product type</div>
        <div className="grid grid-cols-3 gap-0 border-l border-t border-[var(--pf-border)]">
          {[
            ["Business Cards", "3.5 × 2 in", true],
            ["Brochure", "A4 tri-fold", false],
            ["Flyer", "A5", false],
            ["Poster", "A2", false],
            ["Packaging", "custom", false],
            ["Label", "custom", false],
          ].map(([l, s, active]) => (
            <div key={l} className={`p-3 border-r border-b border-[var(--pf-border)] ${active ? "bg-[var(--pf-text)] text-white" : ""}`}>
              <div className="font-bold text-sm">{l}</div>
              <div className={`text-[10px] font-mono mt-1 ${active ? "opacity-70" : "text-[var(--pf-text-2)]"}`}>{s}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="md:col-span-5 border border-[var(--pf-border)] p-5">
        <div className="pf-label mb-3">Live pricing</div>
        <div className="flex items-baseline gap-1">
          <span className="font-mono text-3xl font-black">₹1,062</span>
          <span className="pf-label">.50</span>
        </div>
        <div className="text-xs text-[var(--pf-text-2)] mt-2">500 pcs × ₹2.50 base · 15% bulk tier</div>
        <div className="pf-cmyk-bar mt-6"><span/><span/><span/><span/></div>
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[var(--pf-text-2)]">Industry</span><span>FMCG</span></div>
          <div className="flex justify-between"><span className="text-[var(--pf-text-2)]">Style</span><span>Modern</span></div>
          <div className="flex justify-between"><span className="text-[var(--pf-text-2)]">Colors</span><span>Vibrant</span></div>
          <div className="flex justify-between"><span className="text-[var(--pf-text-2)]">Turnaround</span><span className="font-mono">48h</span></div>
        </div>
      </div>
    </div>
  );
}

/* -------- Stage 2: AI Generate -------- */
function GenerateStage() {
  const [phase, setPhase] = useState("loading"); // loading -> done
  useEffect(() => {
    setPhase("loading");
    const t = setTimeout(() => setPhase("done"), 2600);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="animate-[fadeIn_.4s_ease-out]">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="pf-label mb-1">AI Design Gallery</div>
          <h3 className="text-3xl font-black tracking-tighter">Concepts</h3>
        </div>
        <div className="pf-btn-primary text-sm inline-flex" style={{ pointerEvents: "none" }}>
          <Sparkle size={14} weight="fill" /> {phase === "loading" ? "Generating…" : "Regenerate"}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`border-2 p-2 ${phase === "done" && i === 1 ? "border-[var(--pf-brand)]" : "border-[var(--pf-border)]"}`}>
            <div className="relative aspect-[4/3] bg-[var(--pf-surface-2)] overflow-hidden">
              {phase === "loading" ? (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[var(--pf-surface-2)] via-white to-[var(--pf-surface-2)] bg-[length:200%_100%] animate-[shimmer_1.4s_infinite]">
                  <div className="pf-label">Rendering…</div>
                </div>
              ) : (
                <MockDesign variant={i} />
              )}
              {phase === "done" && i === 1 && (
                <div className="absolute top-2 right-2 bg-[var(--pf-brand)] text-white px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold">
                  Approved
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mt-2 px-1">
              <div className="font-mono text-[10px] text-[var(--pf-text-2)]">#PF-{["A21","B44","C87"][i]}</div>
              {phase === "done" && i !== 1 && (
                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--pf-text-2)]">Approve</div>
              )}
              {phase === "done" && i === 1 && (
                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--pf-brand)] flex items-center gap-1">
                  <CheckCircle size={11} weight="fill" /> Selected
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockDesign({ variant }) {
  // Three distinct CSS-only "designs" that look like business cards
  if (variant === 0) {
    return (
      <div className="w-full h-full relative" style={{ background: "linear-gradient(135deg,#0E0E10 0%,#1F1F23 100%)" }}>
        <div className="absolute inset-4 border border-white/20"></div>
        <div className="absolute bottom-4 left-4 text-white">
          <div className="text-[10px] tracking-[0.3em] opacity-60 font-mono">NORTHWIND</div>
          <div className="text-white text-lg font-black tracking-tight">R. Mehta</div>
          <div className="text-white/60 text-[9px] mt-1">FOUNDER · +91 98••• ••••</div>
        </div>
        <div className="absolute top-4 right-4 w-2 h-6 bg-[var(--pf-brand)]"></div>
      </div>
    );
  }
  if (variant === 1) {
    return (
      <div className="w-full h-full relative" style={{ background: "linear-gradient(135deg,#FFD700 0%,#FF9E00 100%)" }}>
        <div className="absolute top-4 left-4 w-8 h-8 border-2 border-black rounded-full"></div>
        <div className="absolute bottom-4 left-4">
          <div className="text-[10px] tracking-[0.3em] font-mono font-bold">NORTHWIND</div>
          <div className="text-black text-lg font-black tracking-tight">R. MEHTA</div>
          <div className="text-black/70 text-[9px] mt-1">FOUNDER — hello@nw.co</div>
        </div>
        <div className="absolute inset-x-0 top-1/2 h-[1px] bg-black/50"></div>
      </div>
    );
  }
  return (
    <div className="w-full h-full relative bg-white">
      <div className="absolute inset-0 pf-grid-lines opacity-40"></div>
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
        <div className="text-black text-lg font-black tracking-tighter">NW</div>
        <div className="w-4 h-4 bg-[var(--pf-brand)]"></div>
      </div>
      <div className="absolute bottom-4 left-4 right-4">
        <div className="text-black text-base font-black tracking-tight">R. Mehta</div>
        <div className="text-[var(--pf-text-2)] text-[9px] mt-1">FOUNDER · NORTHWIND</div>
        <div className="text-[var(--pf-text-2)] text-[9px] font-mono">hello@northwind.co</div>
      </div>
    </div>
  );
}

/* -------- Stage 3: Order -------- */
function OrderStage() {
  const statuses = ["placed", "in_production", "quality_check", "dispatched", "delivered"];
  const currentIdx = 2;
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 animate-[fadeIn_.4s_ease-out]">
      <div className="md:col-span-7">
        <div className="pf-label mb-1">Order</div>
        <div className="flex items-baseline gap-3 mb-6">
          <h3 className="text-3xl font-black tracking-tighter">#PF-24601</h3>
          <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[var(--pf-brand)] text-white">In production</span>
        </div>
        <div className="border border-[var(--pf-border)] p-4 mb-4">
          <div className="pf-label mb-3">Status timeline</div>
          <div className="grid grid-cols-5 gap-1">
            {statuses.map((s, i) => (
              <div key={s} className="text-center">
                <div className={`h-1 w-full ${i <= currentIdx ? "bg-[var(--pf-brand)]" : "bg-[var(--pf-border)]"}`}></div>
                <div className={`mt-2 text-[9px] uppercase tracking-widest font-bold ${i <= currentIdx ? "text-[var(--pf-text)]" : "text-[var(--pf-text-2)]"}`}>{s.replace('_', ' ')}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="border border-[var(--pf-border)] p-3">
            <div className="pf-label mb-1">Product</div>
            <div className="font-semibold">Business cards</div>
          </div>
          <div className="border border-[var(--pf-border)] p-3">
            <div className="pf-label mb-1">Qty</div>
            <div className="font-mono font-semibold">500</div>
          </div>
          <div className="border border-[var(--pf-border)] p-3">
            <div className="pf-label mb-1">Vendor</div>
            <div className="font-semibold">Mumbai Press</div>
          </div>
        </div>
      </div>
      <div className="md:col-span-5 border border-[var(--pf-text)] p-5">
        <div className="flex justify-between border-b border-[var(--pf-border)] pb-3 mb-3">
          <div>
            <div className="font-black tracking-tighter">TALAIO/AI</div>
            <div className="pf-label mt-1">GST Invoice</div>
          </div>
          <div className="text-right">
            <div className="font-mono font-bold text-sm">PF-24601</div>
            <div className="font-mono text-[10px] text-[var(--pf-text-2)] mt-1">03 · 07 · 2026</div>
          </div>
        </div>
        <div className="mb-3 text-sm">
          <div className="pf-label mb-1">Billed to</div>
          <div className="font-semibold">R. Mehta · Northwind Ltd.</div>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span>Business cards × 500</span><span className="font-mono">₹1,062.50</span></div>
          <div className="flex justify-between text-[var(--pf-text-2)]"><span>GST (18%)</span><span className="font-mono">₹191.25</span></div>
          <div className="flex justify-between border-t border-[var(--pf-text)] pt-2 mt-2 font-black">
            <span>Total</span><span className="font-mono">₹1,253.75</span>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-[var(--pf-text-2)]">
          <FileText size={14} /> Auto-emailed to admin@northwind.co
        </div>
      </div>
    </div>
  );
}
